import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { Component, Element, Event, EventEmitter, Host, Prop, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { CONFIGURATION_EVENT_TYPE, LANGUAGE_CODE } from '@/common/constants';
import { AdditionalParticipant, EnableEventTypes } from '@nylas/core';

/**
 * The `nylas-select-event-type` component is a UI component that allows users to select the event type for the scheduler configuration.
 */
@Component({
  tag: 'nylas-select-event-type',
  styleUrl: 'nylas-select-event-type.scss',
  shadow: true,
})
export class NylasSelectEventType {
  @Element() host!: HTMLNylasSelectEventTypeElement;

  /**
   * @standalone
   * The participant options passed in the additionalParticipants prop
   * from the nylas-scheduler-editor component.
   */
  @Prop() participantOptions?: AdditionalParticipant[];

  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'select-event-type';

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The enable event types.
   */
  @Prop() enableEventTypes?: EnableEventTypes = {
    one_on_one: true,
    collective: true,
    max_fairness: true,
    max_availability: true,
    group: true,
  };

  /**
   * This event is fired when the return to all scheduling pages button is clicked.
   */
  @Event() returnToAllSchedulingPagesEvent!: EventEmitter<void>;

  /**
   * This event is fired when the event type is selected.
   */
  @Event() createEventTypeSelected!: EventEmitter<{ eventType: CONFIGURATION_EVENT_TYPE }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-select-event-type', 'connectedCallback');
  }

  disconnectedCallback() {
    debug('nylas-select-event-type', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-select-event-type', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-select-event-type', 'componentDidLoad');
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-select-event-type', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @RegisterComponent<NylasSelectEventType, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-select-event-type',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.additionalParticipants', 'participantOptions'],
      ['schedulerConfig.enableEventTypes', 'enableEventTypes'],
    ]),
    eventToProps: {
      returnToAllSchedulingPagesEvent: async (_event: CustomEvent<void>, connector: NylasSchedulerConfigConnector) => {
        connector.schedulerConfigStore.state.action = null;
        connector.schedulerConfigStore.set('selectedEventType', undefined);
      },
      createEventTypeSelected: async (event: CustomEvent<{ eventType: CONFIGURATION_EVENT_TYPE }>, connector: NylasSchedulerConfigConnector) => {
        const { eventType } = event.detail;
        connector.schedulerConfigStore.state.action = 'create';
        connector.schedulerConfigStore.state.selectedEventType = eventType;
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    interface EventTypeOption {
      label: string;
      value: CONFIGURATION_EVENT_TYPE;
      description: string;
    }

    const eventTypeKey = {
      'one-on-one': 'one_on_one',
      'collective': 'collective',
      'max-fairness': 'max_fairness',
      'max-availability': 'max_availability',
      'group': 'group',
    };

    const allEventTypes: EventTypeOption[] = [
      {
        label: i18next.t('nylasSelectEventType.eventTypeOptions.oneOnOne.label'),
        value: CONFIGURATION_EVENT_TYPE.one_on_one,
        description: i18next.t('nylasSelectEventType.eventTypeOptions.oneOnOne.desc'),
      },
      {
        label: i18next.t('nylasSelectEventType.eventTypeOptions.collective.label'),
        value: CONFIGURATION_EVENT_TYPE.collective,
        description: i18next.t('nylasSelectEventType.eventTypeOptions.collective.desc'),
      },
      {
        label: i18next.t('nylasSelectEventType.eventTypeOptions.maxFairness.label'),
        value: CONFIGURATION_EVENT_TYPE.max_fairness,
        description: i18next.t('nylasSelectEventType.eventTypeOptions.maxFairness.desc'),
      },
      {
        label: i18next.t('nylasSelectEventType.eventTypeOptions.maxAvailability.label'),
        value: CONFIGURATION_EVENT_TYPE.max_availability,
        description: i18next.t('nylasSelectEventType.eventTypeOptions.maxAvailability.desc'),
      },
      {
        label: i18next.t('nylasSelectEventType.eventTypeOptions.group.label'),
        value: CONFIGURATION_EVENT_TYPE.group,
        description: i18next.t('nylasSelectEventType.eventTypeOptions.group.desc'),
      },
    ];
    const conditionalEventTypes = [CONFIGURATION_EVENT_TYPE.collective, CONFIGURATION_EVENT_TYPE.max_availability, CONFIGURATION_EVENT_TYPE.max_fairness];
    const hasAdditionalParticipants =
      this.participantOptions &&
      this.participantOptions?.length > 0 &&
      !!this.participantOptions[0].email &&
      !!this.participantOptions[0].name &&
      !!this.participantOptions[0].calendars &&
      this.participantOptions[0].calendars.length > 0;

    const eventTypes = this.enableEventTypes
      ? allEventTypes.filter(eventType => {
          const key = eventType.value;
          const keyType = eventTypeKey[key];
          console.log('key', key, this.enableEventTypes ? this.enableEventTypes[keyType] : 'no enableEventTypes');
          if (conditionalEventTypes.includes(key) && !hasAdditionalParticipants) {
            return false;
          }
          return this.enableEventTypes && this.enableEventTypes[keyType];
        })
      : allEventTypes;
    return (
      <Host>
        <div class="nylas-select-event-type" part="nset">
          <div class="header" part="nset__header">
            <h2>{i18next.t('nylasSelectEventType.createEventHeaderTitle')}</h2>
            <button-component
              variant="invisible"
              part="nset__header__back-button"
              onClick={_event => {
                this.returnToAllSchedulingPagesEvent.emit();
              }}
            >
              <chevron-icon width="16" height="16" />
              <span>{i18next.t('nylasSelectEventType.returnToAllSchedulingPages')}</span>
            </button-component>
          </div>
          <div class="content" part="nset__content">
            <h3>{i18next.t('nylasSelectEventType.eventTypeHeaderTitle')}</h3>
            <div class="event-type" part="nset__content__event-type">
              {eventTypes.length === 0 && <p>{i18next.t('nylasSelectEventType.noEventTypesEnabled')}</p>}
              {eventTypes.map(eventType => (
                <div
                  class="event-type-option"
                  onClick={_event => {
                    this.createEventTypeSelected.emit({ eventType: eventType.value });
                  }}
                  part={`nset__content__event-type-option-${eventType.value}`}
                >
                  <h4>{eventType.label}</h4>
                  <p>{eventType.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
